import {ChangeDetectorRef, Component, computed, inject, OnInit, signal} from '@angular/core';
import {HttpBackend, HttpClient} from '@angular/common/http';

import {NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet} from '@angular/router';
import {filter} from 'rxjs';
import {NgbDropdownModule, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {FormsModule} from '@angular/forms';

import {OidcSecurityService} from 'angular-auth-oidc-client';
import {environment} from '../../../environments/environment';
import {LoadingComponent} from '../../shared/commons/loading/loading.component';
import {ToastsContainer} from '../../shared/commons/toast/toast-container.component';
import {BreadcrumbComponent} from '../../shared/commons/breadcrumb/breadcrumb.component';
import {UserCertificateModalComponent} from './user-certificate-modal.component';
import {FloatingChatComponent} from '../../views/floating-chat/floating-chat.component';
import {logoutWithFallback} from '../../shared/auth/logout';
import {
  ADMIN_ROLES,
  extractRoles,
  extractRolesFromAccessToken,
  hasAnyRoleInSet,
  mergeRoles,
  STAFF_ROLES
} from '../../shared/auth/role-access';

type MyUserData = any & { avatar?: string };

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NgbDropdownModule,
    FormsModule,
    LoadingComponent,
    ToastsContainer,
    BreadcrumbComponent,
    FloatingChatComponent
  ],
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.scss']
})
export class AdminLayoutComponent implements OnInit {
  private readonly sidebarCollapsedStorageKey = 'admin-layout.sidebar-collapsed';
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly httpBackend = inject(HttpBackend, {optional: true});
  private readonly http = this.httpBackend ? new HttpClient(this.httpBackend) : null;
  readonly appVersion = signal<string | undefined>(undefined);
  protected oidcSecurityService = inject(OidcSecurityService);
  private readonly modalService = inject(NgbModal);
  private readonly router = inject(Router);

  private readonly routePrefixToSubMenu: { prefix: string; key: string }[] = [
    {prefix: '/certificate-request', key: 'certificateRequest'},
    {prefix: '/import-request', key: 'importerCode'},
    {prefix: '/reports', key: 'report'},
  ];
  isSidebarCollapsed = false;
  isMobileMenuOpen = false;
  isMobileView = signal(typeof window !== "undefined" ? window.innerWidth <= 768 : false);
  readonly isLegacyMode: boolean = true;
  searchQuery = '';
  user: MyUserData = {} as MyUserData;
  private readonly userDataRoles = signal<ReadonlySet<string>>(new Set());
  private readonly accessTokenRoles = signal<ReadonlySet<string>>(new Set());
  private readonly currentRoles = computed(() => mergeRoles(this.userDataRoles(), this.accessTokenRoles()));
  protected readonly isAdmin = computed(() => hasAnyRoleInSet(this.currentRoles(), ADMIN_ROLES));
  protected readonly isStaff = computed(() => hasAnyRoleInSet(this.currentRoles(), STAFF_ROLES));
  showTooltip = false;
  activePopup: string | null = null;
  subMenuStates: { [key: string]: boolean } = {
    'masterData': false,
    'certificateRequest': true,
    'surveyChatArticle': false,
    'settings': false,
    'report': false
  };

  protected getInitial(word: string | undefined): string {
    if (!word) {
      return '';
    }
    return 'เแโใไ'.includes(word[0]) ? (word[1] ?? '') : word[0];
  }

  constructor() {
    this.oidcSecurityService.getUserData()
      .subscribe(userData => {
        this.userDataRoles.set(extractRoles(userData));
        if (userData) {
          this.user.name = userData.name;
          this.user.email = userData.email;
          this.user.avatar = 'https://ui-avatars.com/api/?name=Admin+User&background=0a9b4a&color=fff&size=128';
          this.user.role = 'ผู้ใช้งานระบบ';
        }
      });

    // User-info claims do not always include Keycloak client roles, so also
    // inspect the access token when the OIDC service exposes it.
    const getAccessToken = this.oidcSecurityService.getAccessToken;
    if (typeof getAccessToken === 'function') {
      getAccessToken.call(this.oidcSecurityService).subscribe({
        next: accessToken => this.accessTokenRoles.set(extractRolesFromAccessToken(accessToken)),
        error: () => this.accessTokenRoles.set(new Set()),
      });
    }
  }

  ngOnInit(): void {
    this.isSidebarCollapsed = this.loadSidebarCollapsedState();
    this.loadSubMenuStates();
    this.applyTheme();
    this.checkMobileView();
    this.listenToResize();
    this.loadAppVersion();
    this.expandSubMenuForUrl(this.router.url);
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(e => {
      this.expandSubMenuForUrl((e as NavigationEnd).urlAfterRedirects);
    });
  }

  private loadAppVersion(): void {
    if (!this.http) {
      return;
    }
    const isDevServer = typeof window !== 'undefined' && window.location.port === '4200';
    const baseUrl = isDevServer && environment.baseUrl ? environment.baseUrl.replace(/\/+$/, '') : '';
    const url = baseUrl ? `${baseUrl}/public/api/version` : '/public/api/version';
    this.http.get<{ build?: { version?: string } }>(url).subscribe({
      next: info => {
        queueMicrotask(() => {
          this.appVersion.set(info?.build?.version);
          this.cdr.markForCheck();
        });
      },
      error: () => {
        /* version readout is best-effort; ignore failures */
      },
    });
  }

  private expandSubMenuForUrl(url: string): void {
    const match = this.routePrefixToSubMenu.find(m => url.startsWith(m.prefix));
    if (match && !this.subMenuStates[match.key]) {
      this.subMenuStates[match.key] = true;
      this.saveSubMenuStates();
    }
  }

  get isCollapsedView(): boolean {
    if (typeof window !== 'undefined' && window.innerWidth <= 768) {
      return false;
    }
    return this.isSidebarCollapsed;
  }

  private checkMobileView(): void {
    if (typeof window !== 'undefined') {
      this.isMobileView.set(window.innerWidth <= 768);
    }
  }

  private listenToResize(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', () => {
        const wasMobile = this.isMobileView();
        this.checkMobileView();

        // Close mobile menu if transitioning from mobile to desktop
        if (wasMobile && !this.isMobileView() && this.isMobileMenuOpen) {
          this.isMobileMenuOpen = false;
        }
      });
    }
  }

  private applyTheme() {
    if (typeof document !== 'undefined') {
      document.body.classList.add('theme-legacy');
    }
  }

  toggleSidebar() {
    if (this.isMobileView()) {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
      return;
    }

    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.saveSidebarCollapsedState(this.isSidebarCollapsed);

    // Collapse all submenus when sidebar is collapsed
    if (this.isSidebarCollapsed) {
      Object.keys(this.subMenuStates).forEach(key => {
        this.subMenuStates[key] = false;
      });
      this.saveSubMenuStates();
    }
  }

  closeMobileMenu(): void {
    if (this.isMobileView()) {
      this.isMobileMenuOpen = false;
    }
  }

  toggleSubMenu(key: string) {
    // If sidebar is collapsed, expand it first
    if (this.isCollapsedView) {
      this.toggleSidebar();
      // Wait for sidebar to expand, then open the submenu
      setTimeout(() => {
        this.subMenuStates[key] = true;
        this.saveSubMenuStates();
      }, 150);
      return;
    }
    this.subMenuStates[key] = !this.subMenuStates[key];
    this.saveSubMenuStates();
  }

  private loadSidebarCollapsedState(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return window.localStorage.getItem(this.sidebarCollapsedStorageKey) === 'true';
    } catch {
      return false;
    }
  }

  private saveSidebarCollapsedState(value: boolean): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(this.sidebarCollapsedStorageKey, String(value));
    } catch {
      // Ignore storage failures (private mode / blocked storage).
    }
  }

  private loadSubMenuStates(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      const stored = window.localStorage.getItem('admin-layout.submenu-states');
      if (stored) {
        this.subMenuStates = {...this.subMenuStates, ...JSON.parse(stored)};
      }
    } catch {
      // Ignore storage failures.
    }
  }

  private saveSubMenuStates(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem('admin-layout.submenu-states', JSON.stringify(this.subMenuStates));
    } catch {
      // Ignore storage failures.
    }
  }

  showPopup(key: string): void {
    if (this.isCollapsedView) {
      this.activePopup = key;
    }
  }

  hidePopup(): void {
    this.activePopup = null;
  }

  onSidebarMouseEnter(): void {
    this.showTooltip = this.isCollapsedView;
  }

  onSidebarMouseLeave(): void {
    this.showTooltip = false;
  }

  private redirectTo(url: string): void {
    window.location.assign(url);
  }

  logout(): void {
    logoutWithFallback(this.oidcSecurityService, url => this.redirectTo(url));
  }

  openCertificateInfo(event?: Event): void {
    event?.preventDefault();
    this.modalService.open(UserCertificateModalComponent, {
      size: 'xl',
      centered: true,
      scrollable: true
    });
  }

  protected goToPortalSystem() {
    window.location.href = `${environment.portalUrl}?referrer=${environment.clientId}`;
  }

  protected goToPortalProfile(event?: Event): void {
    event?.preventDefault();
    const portalBaseUrl = (environment.portalConsoleUrl || environment.portalUrl).replace(/\/+$/, '');
    this.redirectTo(`${portalBaseUrl}/ui/profile`);
  }

}
